import { isPlatformServer } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs';
import { randomString } from '../lib/random-string';
import { PICFLOW_CONFIG } from '../picflow-config';
import { ToastService } from './toast.service';

@Injectable({
  providedIn: 'root',
})
export class VersionCheckService {
  private platformId = inject(PLATFORM_ID);
  private config = inject(PICFLOW_CONFIG);
  private http = inject(HttpClient);
  private router = inject(Router);
  private toasts = inject(ToastService);
  private lastVersionCheck = Date.now();

  constructor() {
    this.router.events.pipe(filter(event => event instanceof NavigationStart)).subscribe(() => this.checkVersion());
  }

  private checkVersion = async () => {
    if (Date.now() - this.lastVersionCheck < 1000 * 60 * 5) return;
    if (isPlatformServer(this.platformId)) return;
    this.lastVersionCheck = Date.now();
    if (this.config.env === 'local') return;

    return this.http.get<{ version: string }>(`/internal-version?cacheBust=${randomString(6)}`).subscribe(resp => {
      if (resp.version && resp.version !== window['picflowVersion']) {
        this.handleVersionDifference(resp.version);
      }
    });
  };

  private handleVersionDifference = (version: string) => {
    // 1.1.4-504a69660ded8fe426201fdbf0ccd168fc76ab90
    const currentVersion = window['picflowVersion'] as string;
    const [major, minor, patch] = version.split('-')[0].split('.');
    const [currentMajor, currentMinor, currentPatch] = currentVersion.split('-')[0].split('.');

    if (major > currentMajor || (major === currentMajor && minor > currentMinor)) {
      this.reload(version);
    } else if (patch > currentPatch) {
      this.toasts.add('An improved version of Picflow is available.', undefined, {
        title: 'New Version',
        location: 'bottom-left',
        image: '/assets/icons/special/browser-picflow.svg',
        duration: undefined,
        fontSize: 13,
        titleFontSize: 16,
        cta: {
          color: 'primary',
          text: 'Refresh',
          height: '1',
          size: '1',
          maxWidth: '84px',
          onClick: () => {
            this.reload(version);
          },
        },
      });
    }
  };

  private reload(newVersion: string) {
    const url = window.location.href + (window.location.href.indexOf('?') > -1 ? '&' : '?') + 'version=' + newVersion;
    if (window.onbeforeunload) return;
    location.href = url;
  }
}
